import type { Timestamp } from 'firebase/firestore';

export type PlatformFilter = 'all' | 'android' | 'ios' | 'desktop';

export interface PartnerFilterState {
  platform: PlatformFilter;
  dateFrom: string;
  dateTo: string;
}

export const DEFAULT_PARTNER_FILTERS: PartnerFilterState = {
  platform: 'all',
  dateFrom: '',
  dateTo: '',
};

export type DateRangePreset = '7d' | '30d' | '90d' | 'all';

function toLocalISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Inclusive date range for a quick preset, in the local yyyy-mm-dd format used by date inputs. */
export function getPresetDateRange(
  preset: DateRangePreset,
): { dateFrom: string; dateTo: string } {
  if (preset === 'all') return { dateFrom: '', dateTo: '' };
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { dateFrom: toLocalISODate(from), dateTo: toLocalISODate(to) };
}

/** Returns the preset matching the current from/to values, or null if it's a custom range. */
export function matchDateRangePreset(
  dateFrom: string,
  dateTo: string,
): DateRangePreset | null {
  if (!dateFrom && !dateTo) return 'all';
  for (const preset of ['7d', '30d', '90d'] as const) {
    const r = getPresetDateRange(preset);
    if (r.dateFrom === dateFrom && r.dateTo === dateTo) return preset;
  }
  return null;
}

export type InstallMethod = 'deeplink' | 'fingerprint' | 'manual_code' | 'other';

export interface ReferralClickRow {
  partnerId?: string;
  platform?: string;
  clickedAt?: Timestamp;
  campaignId?: string;
  country?: string;
}

export interface ReferralInstallRow {
  partnerId?: string;
  platform?: string;
  installedAt?: Timestamp;
  method?: string;
  deviceId?: string;
  campaignId?: string;
  country?: string;
  earningsStatus?: string;
  earningsUSD?: number;
}

export interface PurchaseRow {
  partnerId?: string;
  platform?: string;
  purchasedAt?: Timestamp;
  deviceId?: string;
  campaignId?: string;
}

export function firestoreTimestampToDate(
  value: Timestamp | { seconds: number; nanoseconds?: number } | undefined,
): Date | null {
  if (!value) return null;
  if (typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  if (typeof (value as { seconds: number }).seconds === 'number') {
    return new Date((value as { seconds: number }).seconds * 1000);
  }
  return null;
}

function startOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function endOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.getTime();
}

export function matchesDateRange(
  eventDate: Date | null,
  dateFrom: string,
  dateTo: string,
): boolean {
  if (!dateFrom && !dateTo) return true;
  if (!eventDate) return false;

  const t = eventDate.getTime();
  if (dateFrom) {
    const from = new Date(`${dateFrom}T00:00:00`);
    if (t < startOfDay(from)) return false;
  }
  if (dateTo) {
    const to = new Date(`${dateTo}T00:00:00`);
    if (t > endOfDay(to)) return false;
  }
  return true;
}

export function normalizePlatform(platform: string | undefined): string {
  return (platform || 'unknown').toLowerCase();
}

export function matchesPlatform(
  platform: string | undefined,
  filter: PlatformFilter,
): boolean {
  if (filter === 'all') return true;
  return normalizePlatform(platform) === filter;
}

export function resolvePurchasePlatform(
  purchase: PurchaseRow,
  devicePlatformMap: Map<string, string>,
): string | undefined {
  if (purchase.platform) return purchase.platform;
  if (purchase.deviceId) return devicePlatformMap.get(purchase.deviceId);
  return undefined;
}

export function buildDevicePlatformMap(
  referrals: ReferralInstallRow[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const r of referrals) {
    if (r.deviceId && r.platform) {
      map.set(r.deviceId, r.platform);
    }
  }
  return map;
}

export function normalizeInstallMethod(method: string | undefined): InstallMethod {
  if (method === 'deeplink') return 'deeplink';
  if (method === 'fingerprint') return 'fingerprint';
  if (method === 'manual_code') return 'manual_code';
  return 'other';
}

export const INSTALL_METHOD_LABELS: Record<InstallMethod, string> = {
  deeplink: 'Deeplink',
  fingerprint: 'Fingerprint (iOS)',
  manual_code: 'Manual code',
  other: 'Other',
};

export interface FilteredPartnerStats {
  clicks: number;
  installs: number;
  purchases: number;
  clicksIos: number;
  clicksAndroid: number;
  clicksDesktop: number;
  installsIos: number;
  installsAndroid: number;
  purchasesIos: number;
  purchasesAndroid: number;
  installMethods: Record<InstallMethod, number>;
  countryBreakdown: Record<string, number>;
}

export function computeFilteredStats(
  clicks: ReferralClickRow[],
  referrals: ReferralInstallRow[],
  purchases: PurchaseRow[],
  filters: PartnerFilterState,
  options?: { campaignId?: string },
): FilteredPartnerStats {
  const devicePlatformMap = buildDevicePlatformMap(referrals);
  const campaignId = options?.campaignId;

  const filteredClicks = clicks.filter((c) => {
    if (campaignId && c.campaignId !== campaignId) return false;
    if (!matchesPlatform(c.platform, filters.platform)) return false;
    return matchesDateRange(
      firestoreTimestampToDate(c.clickedAt),
      filters.dateFrom,
      filters.dateTo,
    );
  });

  const filteredReferrals = referrals.filter((r) => {
    if (campaignId && r.campaignId !== campaignId) return false;
    if (!matchesPlatform(r.platform, filters.platform)) return false;
    return matchesDateRange(
      firestoreTimestampToDate(r.installedAt),
      filters.dateFrom,
      filters.dateTo,
    );
  });

  const filteredPurchases = purchases.filter((p) => {
    if (campaignId && p.campaignId !== campaignId) return false;
    const platform = resolvePurchasePlatform(p, devicePlatformMap);
    if (!matchesPlatform(platform, filters.platform)) return false;
    return matchesDateRange(
      firestoreTimestampToDate(p.purchasedAt),
      filters.dateFrom,
      filters.dateTo,
    );
  });

  const installMethods: Record<InstallMethod, number> = {
    deeplink: 0,
    fingerprint: 0,
    manual_code: 0,
    other: 0,
  };
  const countryBreakdown: Record<string, number> = {};
  let clicksIos = 0;
  let clicksAndroid = 0;
  let clicksDesktop = 0;
  for (const c of filteredClicks) {
    const platform = normalizePlatform(c.platform);
    if (platform === 'ios') clicksIos += 1;
    else if (platform === 'android') clicksAndroid += 1;
    else if (platform === 'desktop') clicksDesktop += 1;
  }

  let installsIos = 0;
  let installsAndroid = 0;
  for (const r of filteredReferrals) {
    const key = normalizeInstallMethod(r.method);
    installMethods[key] += 1;
    if (r.country) {
      countryBreakdown[r.country] = (countryBreakdown[r.country] || 0) + 1;
    }
    const platform = normalizePlatform(r.platform);
    if (platform === 'ios') installsIos += 1;
    else if (platform === 'android') installsAndroid += 1;
  }

  let purchasesIos = 0;
  let purchasesAndroid = 0;
  for (const p of filteredPurchases) {
    const platform = normalizePlatform(resolvePurchasePlatform(p, devicePlatformMap));
    if (platform === 'ios') purchasesIos += 1;
    else if (platform === 'android') purchasesAndroid += 1;
  }

  return {
    clicks: filteredClicks.length,
    installs: filteredReferrals.length,
    purchases: filteredPurchases.length,
    clicksIos,
    clicksAndroid,
    clicksDesktop,
    installsIos,
    installsAndroid,
    purchasesIos,
    purchasesAndroid,
    installMethods,
    countryBreakdown,
  };
}

export const INSTALL_REWARD_USD = 0.10;
export const MIN_PAYOUT_USD = 10;

export type PayoutDisplayStatus = 'paid' | 'pending_payout' | 'accumulating';
export type PayoutEligibility = 'none' | 'accumulating' | 'eligible';

export function getUnpaidBalance(totalEarnings: number, totalPaid: number): number {
  return Math.max(0, totalEarnings - totalPaid);
}

export function getPayoutEligibility(unpaid: number): PayoutEligibility {
  if (unpaid <= 0) return 'none';
  if (unpaid < MIN_PAYOUT_USD) return 'accumulating';
  return 'eligible';
}

export function getPayoutDisplayStatus(
  monthStatus: string | undefined,
  unpaid: number,
): PayoutDisplayStatus {
  if (monthStatus === 'paid') return 'paid';
  if (unpaid >= MIN_PAYOUT_USD) return 'pending_payout';
  return 'accumulating';
}

export const PAYOUT_DISPLAY_LABELS: Record<PayoutDisplayStatus, string> = {
  paid: 'Paid',
  pending_payout: 'Pending payout',
  accumulating: 'Accumulating',
};

export function getRemainingToMinimum(unpaid: number): number {
  return Math.max(0, MIN_PAYOUT_USD - unpaid);
}

export function getInstallsToMinimum(unpaid: number): number {
  return Math.ceil(getRemainingToMinimum(unpaid) / INSTALL_REWARD_USD);
}

export function isPayoutEligible(unpaid: number): boolean {
  return unpaid >= MIN_PAYOUT_USD;
}

export type CreditStatusFilter = 'all' | 'credited' | 'rejected';
export type PartnerStatusFilter = 'all' | 'active' | 'inactive';
export type PayoutStatusFilter = 'all' | 'pending' | 'paid';
export type DatePreset = 'today' | '7d' | '30d' | 'this_month' | 'last_month' | 'all_time';
export type AdminTableSort = 'installs' | 'clicks' | 'earnings' | 'unpaid' | 'conversion' | 'name';

export interface AdminPartnerRow {
  id: string;
  displayName: string;
  referralCode: string;
  email: string;
  totalEarnings: number;
  totalPaid: number;
  status: string;
}

export interface AdminPayoutRow {
  partnerId: string;
  month: string;
  totalInstalls?: number;
  totalEarnings?: number;
  totalCommission?: number;
  paidAmount: number;
  status?: string;
}

export interface AdminCampaignOption {
  id: string;
  shortCode: string;
  name: string;
  partnerId: string;
}

export interface AdminFilterState extends PartnerFilterState {
  partnerId: 'all' | string;
  partnerStatus: PartnerStatusFilter;
  country: 'all' | string;
  installMethod: 'all' | InstallMethod;
  creditStatus: CreditStatusFilter;
  campaignId: 'all' | string;
  payoutStatus: PayoutStatusFilter;
  minUnpaid: number;
  tableSearch: string;
  tableSort: AdminTableSort;
  tableSortDir: 'asc' | 'desc';
}

export const DEFAULT_ADMIN_FILTERS: AdminFilterState = {
  platform: 'all',
  dateFrom: '',
  dateTo: '',
  partnerId: 'all',
  partnerStatus: 'all',
  country: 'all',
  installMethod: 'all',
  creditStatus: 'all',
  campaignId: 'all',
  payoutStatus: 'all',
  minUnpaid: 0,
  tableSearch: '',
  tableSort: 'installs',
  tableSortDir: 'desc',
};

export interface AdminPartnerTableRow {
  id: string;
  displayName: string;
  referralCode: string;
  email: string;
  status: string;
  clicks: number;
  installs: number;
  purchases: number;
  periodEarnings: number;
  totalPaid: number;
  lifetimeEarnings: number;
  unpaid: number;
  conversion: number;
}

export interface AdminStatsResult {
  totalInstalls: number;
  periodEarnings: number;
  totalPaid: number;
  totalUnpaid: number;
  totalClicks: number;
  totalPurchases: number;
  conversionRate: string;
  partnerCount: number;
  pendingPayouts: number;
  countryBreakdown: Record<string, number>;
  monthlyInstalls: Record<string, number>;
  partnerTable: AdminPartnerTableRow[];
  hasActiveFilters: boolean;
}

function formatDateYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function applyDatePreset(preset: DatePreset): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const today = formatDateYMD(now);

  switch (preset) {
    case 'today':
      return { dateFrom: today, dateTo: today };
    case '7d': {
      const from = new Date(now);
      from.setDate(from.getDate() - 6);
      return { dateFrom: formatDateYMD(from), dateTo: today };
    }
    case '30d': {
      const from = new Date(now);
      from.setDate(from.getDate() - 29);
      return { dateFrom: formatDateYMD(from), dateTo: today };
    }
    case 'this_month':
      return {
        dateFrom: formatDateYMD(new Date(now.getFullYear(), now.getMonth(), 1)),
        dateTo: today,
      };
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { dateFrom: formatDateYMD(start), dateTo: formatDateYMD(end) };
    }
    case 'all_time':
    default:
      return { dateFrom: '', dateTo: '' };
  }
}

function isCreditedReferral(r: ReferralInstallRow): boolean {
  return r.earningsStatus !== 'rejected';
}

function referralPeriodEarnings(r: ReferralInstallRow): number {
  if (r.earningsStatus === 'rejected') return 0;
  if (typeof r.earningsUSD === 'number') return r.earningsUSD;
  return INSTALL_REWARD_USD;
}

function matchesCreditStatus(
  r: ReferralInstallRow,
  filter: CreditStatusFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'rejected') return r.earningsStatus === 'rejected';
  return isCreditedReferral(r);
}

function matchesCountryCode(country: string | undefined, filter: string): boolean {
  if (filter === 'all') return true;
  return country === filter;
}

function monthsInDateRange(dateFrom: string, dateTo: string): Set<string> | null {
  if (!dateFrom && !dateTo) return null;
  const months = new Set<string>();
  const start = dateFrom
    ? new Date(`${dateFrom}T00:00:00`)
    : new Date('2020-01-01');
  const end = dateTo ? new Date(`${dateTo}T00:00:00`) : new Date();
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonth) {
    months.add(cursor.toISOString().slice(0, 7));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

function partnerMatchesFilters(
  partner: AdminPartnerRow,
  filters: AdminFilterState,
): boolean {
  if (filters.partnerId !== 'all' && partner.id !== filters.partnerId) return false;
  if (filters.partnerStatus !== 'all' && partner.status !== filters.partnerStatus) {
    return false;
  }
  const unpaid = partner.totalEarnings - partner.totalPaid;
  if (filters.minUnpaid > 0 && unpaid < filters.minUnpaid) return false;
  if (filters.tableSearch.trim()) {
    const q = filters.tableSearch.trim().toLowerCase();
    const hay = `${partner.displayName} ${partner.referralCode} ${partner.email}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

export function adminHasActiveFilters(filters: AdminFilterState): boolean {
  return (
    filters.platform !== 'all' ||
    Boolean(filters.dateFrom || filters.dateTo) ||
    filters.partnerId !== 'all' ||
    filters.partnerStatus !== 'all' ||
    filters.country !== 'all' ||
    filters.installMethod !== 'all' ||
    filters.creditStatus !== 'all' ||
    filters.campaignId !== 'all' ||
    filters.payoutStatus !== 'all' ||
    filters.minUnpaid > 0 ||
    Boolean(filters.tableSearch.trim())
  );
}

export function computeAdminStats(
  partners: AdminPartnerRow[],
  clicks: ReferralClickRow[],
  referrals: ReferralInstallRow[],
  purchases: PurchaseRow[],
  payouts: AdminPayoutRow[],
  filters: AdminFilterState,
): AdminStatsResult {
  const eligiblePartners = partners.filter((p) => partnerMatchesFilters(p, filters));
  const eligibleIds = new Set(eligiblePartners.map((p) => p.id));

  const campaignFilter =
    filters.campaignId !== 'all' ? filters.campaignId : undefined;

  const filteredClicks = clicks.filter((c) => {
    if (!c.partnerId || !eligibleIds.has(c.partnerId)) return false;
    if (campaignFilter && c.campaignId !== campaignFilter) return false;
    if (!matchesPlatform(c.platform, filters.platform)) return false;
    if (!matchesCountryCode(c.country, filters.country)) return false;
    return matchesDateRange(
      firestoreTimestampToDate(c.clickedAt),
      filters.dateFrom,
      filters.dateTo,
    );
  });

  const filteredReferrals = referrals.filter((r) => {
    if (!r.partnerId || !eligibleIds.has(r.partnerId)) return false;
    if (campaignFilter && r.campaignId !== campaignFilter) return false;
    if (!matchesPlatform(r.platform, filters.platform)) return false;
    if (!matchesCountryCode(r.country, filters.country)) return false;
    if (!matchesCreditStatus(r, filters.creditStatus)) return false;
    if (
      filters.installMethod !== 'all' &&
      normalizeInstallMethod(r.method) !== filters.installMethod
    ) {
      return false;
    }
    return matchesDateRange(
      firestoreTimestampToDate(r.installedAt),
      filters.dateFrom,
      filters.dateTo,
    );
  });

  const devicePlatformMap = buildDevicePlatformMap(referrals);
  const filteredPurchases = purchases.filter((p) => {
    if (!p.partnerId || !eligibleIds.has(p.partnerId)) return false;
    if (campaignFilter && p.campaignId !== campaignFilter) return false;
    const platform = resolvePurchasePlatform(p, devicePlatformMap);
    if (!matchesPlatform(platform, filters.platform)) return false;
    return matchesDateRange(
      firestoreTimestampToDate(p.purchasedAt),
      filters.dateFrom,
      filters.dateTo,
    );
  });

  const partnerClicks: Record<string, number> = {};
  const partnerInstalls: Record<string, number> = {};
  const partnerPurchases: Record<string, number> = {};
  const partnerPeriodEarnings: Record<string, number> = {};

  for (const c of filteredClicks) {
    if (c.partnerId) partnerClicks[c.partnerId] = (partnerClicks[c.partnerId] || 0) + 1;
  }
  for (const r of filteredReferrals) {
    if (!r.partnerId) continue;
    partnerInstalls[r.partnerId] = (partnerInstalls[r.partnerId] || 0) + 1;
    partnerPeriodEarnings[r.partnerId] =
      (partnerPeriodEarnings[r.partnerId] || 0) + referralPeriodEarnings(r);
  }
  for (const p of filteredPurchases) {
    if (p.partnerId) {
      partnerPurchases[p.partnerId] = (partnerPurchases[p.partnerId] || 0) + 1;
    }
  }

  const countryBreakdown: Record<string, number> = {};
  const monthlyInstalls: Record<string, number> = {};
  for (const r of filteredReferrals) {
    if (r.country) {
      countryBreakdown[r.country] = (countryBreakdown[r.country] || 0) + 1;
    }
    const d = firestoreTimestampToDate(r.installedAt);
    if (d) {
      const key = d.toISOString().slice(0, 7);
      monthlyInstalls[key] = (monthlyInstalls[key] || 0) + 1;
    }
  }

  const rangeMonths = monthsInDateRange(filters.dateFrom, filters.dateTo);
  const filteredPayouts = payouts.filter((p) => {
    if (!eligibleIds.has(p.partnerId)) return false;
    if (filters.payoutStatus !== 'all' && p.status !== filters.payoutStatus) return false;
    if (rangeMonths && !rangeMonths.has(p.month)) return false;
    return true;
  });
  const pendingPayouts = filteredPayouts.filter((p) => p.status === 'pending').length;

  let partnerTable: AdminPartnerTableRow[] = eligiblePartners.map((p) => {
    const clicksCount = partnerClicks[p.id] || 0;
    const installsCount = partnerInstalls[p.id] || 0;
    const conversion = clicksCount > 0 ? (installsCount / clicksCount) * 100 : 0;
    return {
      id: p.id,
      displayName: p.displayName,
      referralCode: p.referralCode,
      email: p.email,
      status: p.status,
      clicks: clicksCount,
      installs: installsCount,
      purchases: partnerPurchases[p.id] || 0,
      periodEarnings: partnerPeriodEarnings[p.id] || 0,
      totalPaid: p.totalPaid,
      lifetimeEarnings: p.totalEarnings,
      unpaid: p.totalEarnings - p.totalPaid,
      conversion,
    };
  });

  const sortMult = filters.tableSortDir === 'asc' ? 1 : -1;
  partnerTable = [...partnerTable].sort((a, b) => {
    let cmp = 0;
    switch (filters.tableSort) {
      case 'name':
        cmp = a.displayName.localeCompare(b.displayName);
        break;
      case 'clicks':
        cmp = a.clicks - b.clicks;
        break;
      case 'earnings':
        cmp = a.periodEarnings - b.periodEarnings;
        break;
      case 'unpaid':
        cmp = a.unpaid - b.unpaid;
        break;
      case 'conversion':
        cmp = a.conversion - b.conversion;
        break;
      case 'installs':
      default:
        cmp = a.installs - b.installs;
        break;
    }
    return cmp * sortMult;
  });

  const totalClicks = filteredClicks.length;
  const totalInstalls = filteredReferrals.length;
  const periodEarnings = filteredReferrals.reduce(
    (s, r) => s + referralPeriodEarnings(r),
    0,
  );
  const totalPaid = eligiblePartners.reduce((s, p) => s + p.totalPaid, 0);
  const lifetimeEarningsSum = eligiblePartners.reduce((s, p) => s + p.totalEarnings, 0);

  return {
    totalInstalls,
    periodEarnings,
    totalPaid,
    totalUnpaid: lifetimeEarningsSum - totalPaid,
    totalClicks,
    totalPurchases: filteredPurchases.length,
    conversionRate:
      totalClicks > 0 ? ((totalInstalls / totalClicks) * 100).toFixed(1) : '0',
    partnerCount: eligiblePartners.length,
    pendingPayouts,
    countryBreakdown,
    monthlyInstalls,
    partnerTable,
    hasActiveFilters: adminHasActiveFilters(filters),
  };
}

export function exportAdminTableCsv(rows: AdminPartnerTableRow[]): string {
  const header =
    'Partner,Code,Clicks,Installs,Purchases,Period Earnings,Paid,Unpaid,Status';
  const lines = rows.map(
    (r) =>
      `"${r.displayName.replace(/"/g, '""')}",${r.referralCode},${r.clicks},${r.installs},${r.purchases},${r.periodEarnings.toFixed(2)},${r.totalPaid.toFixed(2)},${r.unpaid.toFixed(2)},${r.status}`,
  );
  return [header, ...lines].join('\n');
}
